import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DateRange, Locale } from "@/shared/utils";
import { useGetCombinedLeaveApplicationsQuery } from "../api";
import type { CombinedLeaveItem, LeaveType } from "../types";
import { useLeaveApplicationsFilter, hasActiveFilters } from "./useLeaveApplicationsFilter";
import { useLeaveApplicationsSort, toggleSort, type SortKey } from "./useLeaveApplicationsSort";
import { useLeaveApplicationsPagination } from "./useLeaveApplicationsPagination";

export interface UseLeaveApplicationsListPageReturn {
  isLoading: boolean;
  isError: boolean;
  allLeaves: CombinedLeaveItem[];
  search: string;
  setSearch: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  statusFilter: LeaveType | null;
  setStatusFilter: (value: LeaveType | null) => void;
  statusOptions: Array<{ label: string; value: LeaveType | null }>;
  activeFilters: boolean;
  view: "table" | "cards";
  setView: (value: "table" | "cards") => void;
  locale: Locale;
  sortConfig: { key: SortKey; direction: "asc" | "desc" } | null;
  handleSort: (key: SortKey) => void;
  filteredLeaves: CombinedLeaveItem[];
  sortedLeaves: CombinedLeaveItem[];
  pagination: ReturnType<typeof useLeaveApplicationsPagination>;
  pageLeaves: CombinedLeaveItem[];
  handleResetFilters: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useLeaveApplicationsListPage(): UseLeaveApplicationsListPageReturn {
  const { i18n, t } = useTranslation("LeaveApplicationsPage");
  const locale = i18n.language as Locale;

  const { data, isLoading, isError } = useGetCombinedLeaveApplicationsQuery();

  const allLeaves = data ?? [];

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<LeaveType | null>(null);

  const statusOptions = useMemo<Array<{ label: string; value: LeaveType | null }>>(() => {
    return [
      { label: t("filters.allLeaveTypes"), value: null },
      { label: t("filters.statusOptions.annual"), value: "annual" },
      { label: t("filters.statusOptions.unpaid"), value: "unpaid" },
      { label: t("filters.statusOptions.medical"), value: "medical" },
      { label: t("filters.statusOptions.business_trip"), value: "business_trip" },
    ];
  }, [t]);

  const [view, setView] = useState<"table" | "cards">("cards");
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: "asc" | "desc" } | null>(null);

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const filterState = useMemo(
    () => ({
      search,
      dateRange,
      statusFilter,
    }),
    [search, dateRange, statusFilter],
  );

  const filteredLeaves = useLeaveApplicationsFilter(allLeaves, filterState);
  const sortedLeaves = useLeaveApplicationsSort(filteredLeaves, sortConfig);

  const activeFilters = hasActiveFilters(filterState);

  const pagination = useLeaveApplicationsPagination(filteredLeaves.length, 10, [search, dateRange, statusFilter]);

  const pageLeaves = sortedLeaves.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
    pagination.setPage(1);
  }

  return {
    isLoading,
    isError,
    allLeaves,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    view,
    setView,
    locale,
    sortConfig,
    handleSort,
    filteredLeaves,
    sortedLeaves,
    pagination,
    pageLeaves,
    handleResetFilters,
    t,
  };
}
